// Organization structured data
export const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Setgo Innovations Private Limited",
    "legalName": "Setgo Innovations Private Limited",
    "description": "Transforming India's youth and micro-business ecosystem through innovation in Edutech, Fintech, and Jobtech",
    "url": "https://setgo.in",
    "logo": {
        "@type": "ImageObject",
        "url": "https://setgo.in/logo.png",
        "width": 200,
        "height": 60
    },
    "identifier": "U85499KL2025PTC092559",
    "contactPoint": {
        "@type": "ContactPoint",
        "email": "contact@setgo.in",
        "contactType": "customer service",
        "availableLanguage": ["English", "Hindi", "Malayalam"]
    },
    "address": {
        "@type": "PostalAddress",
        "addressLocality": "Kerala",
        "addressRegion": "Kerala",
        "addressCountry": "IN"
    },
    "sameAs": [
        "https://twitter.com/setgoinnovations",
        "https://linkedin.com/company/setgo-innovations",
        "https://facebook.com/setgoinnovations"
    ],
    "foundingDate": "2025",
    "areaServed": {
        "@type": "Country",
        "name": "India"
    }
}

// Product/Service structured data
export const productSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Educational Technology, Financial Technology, Job Technology",
    "provider": {
        "@type": "Organization",
        "name": "Setgo Innovations Private Limited"
    },
    "areaServed": {
        "@type": "Country",
        "name": "India"
    },
    "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Setgo Services",
        "itemListElement": [
            {
                "@type": "Offer",
                "itemOffered": {
                    "@type": "Service",
                    "name": "SetGo - Edutech",
                    "description": "Skill-based learning and internships with industry mentorship"
                }
            },
            {
                "@type": "Offer",
                "itemOffered": {
                    "@type": "Service",
                    "name": "Digistore Pay - Fintech",
                    "description": "Digital payment solutions and shop digitalization"
                }
            },
            {
                "@type": "Offer",
                "itemOffered": {
                    "@type": "Service",
                    "name": "Easy Jobs - Jobtech",
                    "description": "Job placement and digital work allocation platform"
                }
            }
        ]
    },
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "850",
        "bestRating": "5",
        "worstRating": "1"
    }
}

// Website structured data
export const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Setgo Innovations",
    "url": "https://setgo.in",
    "description": "Empowering India through Edutech, Fintech, and Jobtech - Making India digitally literate, financially empowered, and job-ready",
    "publisher": {
        "@type": "Organization",
        "name": "Setgo Innovations Private Limited",
        "identifier": "U85499KL2025PTC092559"
    },
    "inLanguage": ["en-IN", "hi-IN", "ml-IN"]
}

// FAQ structured data for common questions
export const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
        {
            "@type": "Question",
            "name": "What is Setgo Innovations?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Setgo Innovations Private Limited is a technology company transforming India's youth and micro-business ecosystem through innovation in Edutech, Fintech, and Jobtech. We are registered under CIN: U85499KL2025PTC092559 and headquartered in Kerala."
            }
        },
        {
            "@type": "Question",
            "name": "What services does Setgo provide?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Setgo offers three main pillars: SetGo (Edutech) for skill-based learning and internships, Digistore Pay (Fintech) for digital payment solutions and shop digitalization, and Easy Jobs (Jobtech) for job placement and work allocation."
            }
        },
        {
            "@type": "Question",
            "name": "How can students benefit from SetGo?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Students can benefit from our learn and earn model with hands-on training, industry mentorship, real-time work experience, and college-industry integration programs that prepare them for the job market."
            }
        },
        {
            "@type": "Question",
            "name": "Can small businesses use Digistore Pay?",
            "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, Digistore Pay is specifically designed for small retail shops and micro-entrepreneurs to enable digital payments, customer management, and financial literacy to help grow their businesses."
            }
        }
    ]
}

const DEFAULT_BASE_URL = "https://setgo.in";

export function buildLocalBusinessSchema(
  name: string,
  slug: string,
  options: {
    description?: string;
    image?: string;
    address?: string;
    telephone?: string;
    rating?: number;
    reviewCount?: number;
    priceRange?: string;
    areaServed?: string;
    geo?: { latitude: number; longitude: number };
  } = {}
) {
  const url = `${DEFAULT_BASE_URL}/businesses/${slug}`;
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name,
    url,
    ...(options.description && { description: options.description }),
    ...(options.image && { image: options.image }),
    ...(options.address && {
      address: {
        "@type": "PostalAddress",
        streetAddress: options.address,
        addressRegion: options.areaServed,
        addressCountry: "IN",
      },
    }),
    ...(options.geo && {
      geo: {
        "@type": "GeoCoordinates",
        latitude: options.geo.latitude,
        longitude: options.geo.longitude,
      },
    }),
    ...(options.telephone && { telephone: options.telephone }),
    ...(options.rating != null &&
      options.reviewCount != null &&
      options.reviewCount > 0 && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: String(options.rating),
          reviewCount: String(options.reviewCount),
          bestRating: "5",
          worstRating: "1",
        },
      }),
    ...(options.priceRange && { priceRange: options.priceRange }),
    ...(options.areaServed && { areaServed: options.areaServed }),
  };
  return schema;
}